import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import moment, { Moment } from "moment";
import { 
  Tooltip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Typography
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { EditorPlanData, AugmentedEditorProject, ProjectHighlightColor } from "./useEditorPlan";
import { useFormats } from "../../../../toolympus/components/schemed";

interface Props {
  data: EditorPlanData;
}

export const EditorPlan: React.FC<Props> = ({ data }) => {
  const { projects, updateProject, settings } = data;

  const [currentMonth, setCurrentMonth] = useState<Moment>(() => moment().startOf("month"));

  // Start Date edit modal state
  const [editingProject, setEditingProject] = useState<AugmentedEditorProject | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<string>("");

  const today = useMemo(() => moment().startOf("day"), []);

  // Generate full calendar weeks covering the month (Monday to Sunday)
  const calendarDays = useMemo(() => {
    const start = currentMonth.clone().startOf("month").startOf("isoWeek");
    const end = currentMonth.clone().endOf("month").endOf("isoWeek");
    const days: Moment[] = [];

    const curr = start.clone();
    while (curr.isSameOrBefore(end, "day")) {
      days.push(curr.clone());
      curr.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  const { formatDate } = useFormats();

  // Month navigation
  const handlePrevMonth = () => setCurrentMonth((prev) => prev.clone().subtract(1, "month"));
  const handleNextMonth = () => setCurrentMonth((prev) => prev.clone().add(1, "month"));
  const handleToday = () => setCurrentMonth(moment().startOf("month"));

  // Focus view on project timeline
  const handleFocusProject = (e: React.MouseEvent, project: AugmentedEditorProject) => {
    e.stopPropagation();
    const targetDate = project.barStartDate || project.editing_start_date || project.deadline;
    if (targetDate) {
      setCurrentMonth(moment(targetDate).startOf("month"));
    }
  };

  // Modal actions
  const handleOpenDateModal = (project: AugmentedEditorProject) => {
    setEditingProject(project);
    setSelectedStartDate(project.editing_start_date || project.barStartDate || moment().format("YYYY-MM-DD"));
  };

  const handleCloseDateModal = () => {
    setEditingProject(null);
    setSelectedStartDate("");
  };

  const handleSaveStartDate = async () => {
    if (!editingProject) return;
    await updateProject({
      _id: editingProject._id,
      editing_start_date: selectedStartDate || null,
    });
    handleCloseDateModal();
  };

  const handleClearStartDate = async () => {
    if (!editingProject) return;
    await updateProject({
      _id: editingProject._id,
      editing_start_date: null,
    });
    handleCloseDateModal();
  };

  return (
    <Container>
      {/* Top Toolbar */}
      <Toolbar>
        <NavGroup>
          <IconButton size="small" onClick={handlePrevMonth}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <MonthTitle>{currentMonth.format("MMMM YYYY")}</MonthTitle>
          <IconButton size="small" onClick={handleNextMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <TodayButton size="small" startIcon={<TodayIcon />} onClick={handleToday}>
            Сегодня
          </TodayButton>
        </NavGroup>

        <LegendGroup>
          <LegendItem>
            <LegendSquare color="#d32f2f" /> Просрочен дедлайн
          </LegendItem>
          <LegendItem>
            <LegendSquare color="#ed6c02" /> Нарушен люфт / Пересечение
          </LegendItem>
          <LegendItem>
            <LegendSquare color="#757575" /> Нет размера
          </LegendItem>
          <LegendItem>
            <DeadlineDot /> Дедлайн
          </LegendItem>
          {settings.editing_buffer_days ? (
            <LegendItem>
              <BufferLine /> Люфт ({settings.editing_buffer_days} дн.)
            </LegendItem>
          ) : null}
        </LegendGroup>
      </Toolbar>

      {/* Gantt Table */}
      <TableWrapper>
        <CalendarTable>
          <thead>
            <tr>
              <ProjectHeaderCell>Проект / Объем</ProjectHeaderCell>
              {calendarDays.map((day) => {
                const isWeekend = day.isoWeekday() > 5;
                const isToday = day.isSame(today, "day");

                return (
                  <DayHeaderCell 
                    key={day.format("YYYY-MM-DD")} 
                    isWeekend={isWeekend} 
                    isToday={isToday}
                  >
                    <DayHeaderNumber isWeekend={isWeekend}>
                      {day.format("D")}
                    </DayHeaderNumber>
                  </DayHeaderCell>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const { statusFlags, highlightColor, barStartDate, barEndDate } = project;

              const deadlineStr = project.deadline ? moment(project.deadline).format("YYYY-MM-DD") : null;
              const bufferStr = project.effectiveDeadline;
              const factor = project.complexityFactor ?? 1.0;

              return (
                <ProjectTr key={project._id} highlightColor={highlightColor}>
                  {/* Sticky Project Info Cell */}
                  <ProjectNameCell highlightColor={highlightColor}>
                    <ProjectCellContainer>
                      {/* Top line: Code, Title Link & Status icons */}
                      <ProjectTitleRow>
                        <Tooltip title={`${project.title}. Дедлайн: ${formatDate(project.deadline)}`}>
                          <ProjectLink 
                            href={`/p?pid=${project._id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ProjectCode>{project.code}</ProjectCode>
                            <ProjectTitleText>{project.title}</ProjectTitleText>
                            <ExternalIcon />
                          </ProjectLink>
                        </Tooltip>

                        <StatusIcons>
                          {statusFlags.overdueDeadline && (
                            <Tooltip title="Дедлайн нарушен!" arrow>
                              <ErrorOutlineIcon style={{ fontSize: 15, color: "#d32f2f" }} />
                            </Tooltip>
                          )}
                          {statusFlags.overdueBuffer && !statusFlags.overdueDeadline && (
                            <Tooltip title="Сдвиг залезает на буферный люфт" arrow>
                              <WarningAmberIcon style={{ fontSize: 15, color: "#ed6c02" }} />
                            </Tooltip>
                          )}
                          {statusFlags.hasOverlap && (
                            <Tooltip title="Пересекается с другим проектом" arrow>
                              <WarningAmberIcon style={{ fontSize: 15, color: "#ed6c02" }} />
                            </Tooltip>
                          )}
                          {statusFlags.missingSize && (
                            <Tooltip title="Не указан размер для расчета сроков" arrow>
                              <EventBusyIcon style={{ fontSize: 15, color: "#757575" }} />
                            </Tooltip>
                          )}
                        </StatusIcons>
                      </ProjectTitleRow>

                      {/* Bottom line: Focus Button + Complexity rounded to 2 places */}
                      <ProjectMetaRow>
                        <Tooltip title="Показать на шкале" arrow>
                          <FocusIconButton 
                            size="small" 
                            onClick={(e) => handleFocusProject(e, project)}
                          >
                            <CenterFocusStrongIcon style={{ fontSize: 13 }} />
                          </FocusIconButton>
                        </Tooltip>

                        {project.editing_size ? (
                          <SizeFormulaText>
                            {project.editing_size} стр. × {factor.toFixed(2)} = {project.durationWorkDays} раб. дн.
                          </SizeFormulaText>
                        ) : (
                          <SizeFormulaText muted>размер не указан</SizeFormulaText>
                        )}
                      </ProjectMetaRow>
                    </ProjectCellContainer>
                  </ProjectNameCell>

                  {/* Day Grid Cells */}
                  {calendarDays.map((day) => {
                    const dateStr = day.format("YYYY-MM-DD");
                    const isWeekend = day.isoWeekday() > 5;
                    const isToday = day.isSame(today, "day");

                    const isDeadlineDay = deadlineStr === dateStr;
                    const isBufferTargetDay = bufferStr === dateStr && bufferStr !== deadlineStr;

                    // Bar boundaries check
                    let isInBar = false;
                    let isBarStart = false;
                    let isBarEnd = false;

                    if (barStartDate && barEndDate) {
                      const mStart = moment(barStartDate);
                      const mEnd = moment(barEndDate);
                      isInBar = day.isBetween(mStart, mEnd, "day", "[]");
                      isBarStart = day.isSame(mStart, "day");
                      isBarEnd = day.isSame(mEnd, "day");
                    }

                    return (
                      <DayCell
                        key={dateStr}
                        isWeekend={isWeekend}
                        isToday={isToday}
                        onClick={() => {
                          if (!statusFlags.missingSize) {
                            handleOpenDateModal(project);
                          }
                        }}
                      >
                        {/* Gantt Bar */}
                        {isInBar && (
                          <Tooltip
                            title={
                              project.isManualStart
                                ? `Ручной старт: ${formatDate(barStartDate)} → ${formatDate(barEndDate)}. Кликните для изменения даты.`
                                : project.statusFlags.isStartMovedToToday
                                  ? `Авторасчет от сегодня, так как авторасчет от дедлайна (${formatDate(project.deadline)}) попадает в прошлое. Кликните для фиксации старта.`
                                  : `Авторасчет от дедлайна: ${formatDate(barStartDate)} → ${formatDate(barEndDate)}. Кликните для фиксации старта.`
                            }
                            arrow
                          >
                            <TaskBar
                              isStart={isBarStart}
                              isEnd={isBarEnd}
                              highlightColor={highlightColor}
                            >
                              {isBarStart && (
                                <TaskBarLabel>
                                  {project.isManualStart ? "★ " : ""}
                                  {project.durationWorkDays}д
                                </TaskBarLabel>
                              )}
                            </TaskBar>
                          </Tooltip>
                        )}

                        {/* Buffer Marker */}
                        {isBufferTargetDay && (
                          <Tooltip title={`Граница люфта (${settings.editing_buffer_days} раб. дн. до дедлайна)`} arrow>
                            <BufferMarker />
                          </Tooltip>
                        )}

                        {/* Deadline Marker */}
                        {isDeadlineDay && (
                          <Tooltip title={`Дедлайн: ${deadlineStr}`} arrow>
                            <DeadlineMarker />
                          </Tooltip>
                        )}
                      </DayCell>
                    );
                  })}
                </ProjectTr>
              );
            })}
          </tbody>
        </CalendarTable>
      </TableWrapper>

      {/* Date Edit Modal */}
      <Dialog open={Boolean(editingProject)} onClose={handleCloseDateModal} maxWidth="xs" fullWidth>
        <DialogTitle>Дата начала редактуры</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, marginTop: 8 }}>
            Проект: <strong>{editingProject?.code}</strong> — {editingProject?.title}
          </Typography>
          <TextField
            label="Дата начала"
            type="date"
            fullWidth
            value={selectedStartDate}
            onChange={(e) => setSelectedStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions style={{ justifyContent: "space-between", padding: "8px 24px 16px" }}>
          {editingProject?.editing_start_date ? (
            <Button color="inherit" onClick={handleClearStartDate} size="small">
              Сбросить на авто
            </Button>
          ) : <div />}
          <div>
            <Button onClick={handleCloseDateModal} color="inherit">
              Отмена
            </Button>
            <Button onClick={handleSaveStartDate} variant="contained" color="primary">
              Сохранить
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// -----------------------------------------------------------------------------
// Styled Components
// -----------------------------------------------------------------------------

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 12px;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MonthTitle = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  min-width: 140px;
  text-align: center;
  text-transform: capitalize;
`;

const TodayButton = styled(Button)`
  text-transform: none;
  font-size: 0.8rem;
  margin-left: 8px;
`;

const LegendGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.75rem;
  color: #666;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendSquare = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(p) => p.color};
`;

const DeadlineDot = styled.span`
  width: 4px;
  height: 14px;
  background-color: #d32f2f;
  border-radius: 2px;
`;

const BufferLine = styled.span`
  width: 2px;
  height: 14px;
  border-right: 2px dashed #ed6c02;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow: auto;
  max-height: 75vh;
`;

const CalendarTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;

const ProjectHeaderCell = styled.th`
  width: 310px;
  min-width: 310px;
  padding: 6px 12px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 4;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  color: #555;
`;

const DayHeaderCell = styled.th<{ isWeekend: boolean; isToday: boolean }>`
  width: 24px;
  min-width: 24px;
  padding: 4px 0;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${(p) => {
    if (p.isToday) return "#e3f2fd";
    if (p.isWeekend) return "#f5f5f5";
    return "#fafafa";
  }};
`;

const DayHeaderNumber = styled.div<{ isWeekend: boolean }>`
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.1;
  color: ${(p) => (p.isWeekend ? "#d32f2f" : "#333")};
`;

const getRowBackgroundColor = (color: ProjectHighlightColor): string => {
  switch (color) {
    case "red":
      return "rgba(244, 67, 54, 0.12)";
    case "orange":
      return "rgba(255, 152, 0, 0.15)";
    case "dark-gray":
      return "rgba(117, 117, 117, 0.12)";
    default:
      return "transparent";
  }
};

const ProjectTr = styled.tr<{ highlightColor: ProjectHighlightColor }>`
  background-color: ${(p) => getRowBackgroundColor(p.highlightColor)};
  transition: background-color 0.15s;

  &:hover {
    background-color: ${(p) => {
      if (p.highlightColor === "red") return "rgba(244, 67, 54, 0.2)";
      if (p.highlightColor === "orange") return "rgba(255, 152, 0, 0.25)";
      if (p.highlightColor === "dark-gray") return "rgba(117, 117, 117, 0.2)";
      return "rgba(25, 118, 210, 0.06)";
    }};
  }
`;

const ProjectNameCell = styled.td<{ highlightColor: ProjectHighlightColor }>`
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 8px 4px 12px;
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: ${(p) => (p.highlightColor !== "none" ? getRowBackgroundColor(p.highlightColor) : "#fff")};
`;

const ProjectCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 38px;
  gap: 1px;
`;

const ProjectTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: #1976d2;
    text-decoration: underline;
  }
`;

const ProjectCode = styled.span`
  font-weight: 700;
  font-size: 0.76rem;
  margin-right: 5px;
  flex-shrink: 0;
`;

const ProjectTitleText = styled.span`
  font-size: 0.76rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExternalIcon = styled(OpenInNewIcon)`
  font-size: 11px !important;
  margin-left: 4px;
  opacity: 0.45;
  flex-shrink: 0;
`;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: 4px;
  flex-shrink: 0;
`;

const ProjectMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FocusIconButton = styled(IconButton)`
  padding: 1px !important;
  color: #757575 !important;
  &:hover {
    color: #1976d2 !important;
    background-color: rgba(25, 118, 210, 0.08) !important;
  }
`;

const SizeFormulaText = styled.span<{ muted?: boolean }>`
  font-size: 0.68rem;
  color: ${(p) => (p.muted ? "#9e9e9e" : "#616161")};
  white-space: nowrap;
  font-family: monospace;
`;

const DayCell = styled.td<{ isWeekend: boolean; isToday: boolean }>`
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  height: 40px;
  position: relative;
  padding: 0;
  cursor: pointer;
  background-color: ${(p) => {
    if (p.isToday) return "rgba(25, 118, 210, 0.08)";
    if (p.isWeekend) return "rgba(0, 0, 0, 0.035)";
    return "transparent";
  }};
`;

const TaskBar = styled.div<{ 
  isStart: boolean; 
  isEnd: boolean; 
  highlightColor: ProjectHighlightColor;
}>`
  position: absolute;
  top: 7px;
  bottom: 7px;
  left: 0;
  right: 0;
  z-index: 1;

  background-color: ${(p) => {
    if (p.highlightColor === "red") return "#ef5350";
    if (p.highlightColor === "orange") return "#ffa726";
    return "#42a5f5";
  }};

  border-top-left-radius: ${(p) => (p.isStart ? "4px" : "0")};
  border-bottom-left-radius: ${(p) => (p.isStart ? "4px" : "0")};
  border-top-right-radius: ${(p) => (p.isEnd ? "4px" : "0")};
  border-bottom-right-radius: ${(p) => (p.isEnd ? "4px" : "0")};

  margin-left: ${(p) => (p.isStart ? "2px" : "0")};
  margin-right: ${(p) => (p.isEnd ? "2px" : "0")};

  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;

  &:hover {
    filter: brightness(0.92);
  }
`;

const TaskBarLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: #fff;
  padding-left: 3px;
  white-space: nowrap;
  overflow: hidden;
  pointer-events: none;
`;

const DeadlineMarker = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  right: 1px;
  width: 4px;
  background-color: #d32f2f;
  border-radius: 2px;
  z-index: 2;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
`;

const BufferMarker = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  right: 1px;
  width: 0;
  border-right: 2px dashed #ed6c02;
  z-index: 2;
`;